<template>
	<div class="d3-blood-pressure-gauges">
		<div class="gauges" ref="container">
			<svg :width="width" :height="height">
				<g :style="{ transform: `translate(${width/2}px, ${height/2}px) rotate(${angle_rotation}rad)` }">
					<path class="back systolic" :d="paths.back_systolic" :class="{ fade: highlight.diastolic }"/>
					<path class="back diastolic" :d="paths.back_diastolic" :class="{ fade: highlight.systolic }"/>
					<path class="front systolic" :d="paths.front_systolic" :class="{ fade: highlight.diastolic }"/>
					<path class="front diastolic" :d="paths.front_diastolic" :class="{ fade: highlight.systolic }"/>
				</g>
				<g class="axis systolic" :style="{ transform: `translate(${width/2}px, ${height/2}px)` }">
				</g>
				<g class="axis diastolic" :style="{ transform: `translate(${width/2}px, ${height/2}px)` }">
				</g>
			</svg>
			<div class="values">
				<div class="systolic" :class="{ fade: highlight.diastolic }">{{ values.systolic }}</div>
				<div class="diastolic" :class="{ fade: highlight.systolic }">{{ values.diastolic }}</div>
				<div class="unit">mmHg</div>
			</div>
		</div>
		<div class="buttons">
			<button @click="onBtnSystolic()" :class="{ highlight: highlight.systolic }">
				<svgicon name="my idicare" :fill="false"/>
				<span>Systolic</span>
			</button>
			<button @click="onBtnDiastolic()" :class="{ highlight: highlight.diastolic }">
				<svgicon name="my idicare" :fill="false"/>
				<span>Diastolic</span>
			</button>
		</div>
	</div>
</template>

<script>
import * as d3 from '@/utils/d3'
Math.radians = function (degrees) {
	return degrees * Math.PI / 180
}
Math.degrees = function (radians) {
	return radians * 180 / Math.PI
}
const props = {
	values: {
		type: Object,
		default: () => ({
			systolic: 0,
			diastolic: 0
		})
	}
}
export default {
	props,
	data () {
		return {
			width: 0,
			height: 0,
			paths: {
				back_systolic: '',
				back_diastolic: '',
				front_systolic: '',
				front_diastolic: ''
			},
			highlight: {
				systolic: false,
				diastolic: false
			}
		}
	},
	computed: {
		angle_range () {
			return Math.radians(340)
		},
		angle_rotation () {
			return -this.angle_range / 2
		},
		scale () {
			const min = Math.min(this.values.systolic, this.values.diastolic, 0)
			const max = Math.max(this.values.systolic, this.values.diastolic, 200) // 200 max absolute value
			const scale = d3.scaleLinear()
				.domain([min, max])
				.range([0, 100])
			return scale
		},
		arc_scale () {
			const min = Math.min(this.values.systolic, this.values.diastolic, 0)
			const max = Math.max(this.values.systolic, this.values.diastolic, 200) // 200 max absolute value
			const scale = d3.scaleLinear()
				.domain([min, max])
				.range([0, this.angle_range])
			return scale
		}
	},
	watch: {
		values: {
			handler () {
				this.update()
			},
			deep: true
		}
	},
	methods: {
		onBtnSystolic () {
			this.highlight.systolic = !this.highlight.systolic
			this.highlight.diastolic = false
		},
		onBtnDiastolic () {
			this.highlight.diastolic = !this.highlight.diastolic
			this.highlight.systolic = false
		},
		onResize () {
			//console.log('D3BloodPressure.onResize')
			this.width = Math.max(this.$refs.container.offsetWidth, 0)
			this.height = Math.max(this.$refs.container.offsetHeight, 0)
			this.update()
		},
		update () {
			const size = Math.min(this.width, this.height)
			const innerRadius = (size / 2) - 20
			const outerRadius = (size / 2) - 5
			const ticks = 20

			const back_systolic = d3.arc()
				.innerRadius(innerRadius)
				.outerRadius(outerRadius)
				.cornerRadius(20)
				.startAngle(0)
				.endAngle(this.angle_range)
			
			const back_diastolic = d3.arc()
				.innerRadius(innerRadius - 25)
				.outerRadius(outerRadius - 25)
				.cornerRadius(20)
				.startAngle(0)
				.endAngle(this.angle_range)

			const front_systolic = d3.arc()
				.innerRadius(innerRadius - 1)
				.outerRadius(outerRadius + 1)
				.cornerRadius(20)
				.startAngle(0)
				.endAngle(this.angle_range * (this.scale(this.values.systolic) / 100))

			const front_diastolic = d3.arc()
				.innerRadius(innerRadius - 25 - 1)
				.outerRadius(outerRadius - 25 + 1)
				.cornerRadius(20)
				.startAngle(0)
				.endAngle(this.angle_range * (this.scale(this.values.diastolic) / 100))

			this.paths.back_systolic = back_systolic()
			this.paths.back_diastolic = back_diastolic()
			this.paths.front_systolic = front_systolic()
			this.paths.front_diastolic = front_diastolic()

			/* SCALE TICKS
			d3.selectAll('.axis.systolic .ticks').remove()
			const axis_systolic = d3.select('.axis.systolic')
			axis_systolic.selectAll('.ticks').data(this.scale.ticks(ticks))
        .enter()
				.append('text')
				.attr('class', 'ticks')
        .style('font-size', '18px')
        .style('text-anchor', 'middle')
				.attr('x', d => Math.cos(this.arc_scale(d) - Math.PI/2 + this.angle_rotation) * (outerRadius - 10))
				.attr('y', d => Math.sin(this.arc_scale(d) - Math.PI/2 + this.angle_rotation) * (outerRadius - 10) + 4)
				.text((d, i) => {
					var v = ''
					if (i > 0 && i < ticks) v = d
					return v
				}) */
		}
	},
	mounted () {
		window.addEventListener('resize', this.onResize)
		this.$root.$on('force-render-biometric-measure', this.onResize)
		this.onResize()
	},
	beforeDestroy () {
		window.removeEventListener('resize', this.onResize)
		this.$root.$off('force-render-biometric-measure', this.onResize)
	}
}
</script>



// WEBPACK FOOTER //
// src/components/watch/readings/biometric/widgets/measure/D3BloodPressure.vue